import { captureMessage } from '@sentry/react';

import type { AccessRule, Scope } from './domain/rules';
import type { BranchDecision } from './routing/routeAccess';

export type AccessReport =
  | { readonly kind: 'would-deny'; readonly rule: string; readonly scope: string; readonly reason: string }
  | { readonly kind: 'refused-after-allow'; readonly url: string; readonly code: string | null }
  | { readonly kind: 'unknown-keys'; readonly keys: readonly string[] };

const REPORTED_AS = { tags: { feature: 'access' } };

const HOLD_RENDER_REPORTS_MS = 5_000;

const reported = new Set<string>();

let held: AccessReport[] = [];

let flushTimer: ReturnType<typeof setTimeout> | null = null;

const send = (report: AccessReport): void => {
  captureMessage(`access.${report.kind}`, { level: 'warning', ...REPORTED_AS, extra: { ...report } });
};

export const flushAccessReports = (): void => {
  if (flushTimer !== null) clearTimeout(flushTimer);
  flushTimer = null;
  const batch = held;
  held = [];
  batch.forEach(send);
};

const flushWhenHidden = (): void => {
  if (document.visibilityState === 'hidden') flushAccessReports();
};

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flushAccessReports);
  document.addEventListener('visibilitychange', flushWhenHidden);
}

export const reportAccess = (report: AccessReport): void => {
  const fingerprint = JSON.stringify(report);
  if (reported.has(fingerprint)) return;
  reported.add(fingerprint);
  if (report.kind !== 'would-deny') {
    send(report);
    return;
  }
  held.push(report);
  if (flushTimer === null) flushTimer = setTimeout(flushAccessReports, HOLD_RENDER_REPORTS_MS);
};

export const resetAccessReports = (): void => {
  reported.clear();
  held = [];
  if (flushTimer !== null) clearTimeout(flushTimer);
  flushTimer = null;
};

export const describeRule = (rule: AccessRule): string => {
  switch (rule.kind) {
    case 'signedIn':
      return 'signedIn';
    case 'job':
    case 'admin':
    case 'exception':
      return `${rule.kind}:${rule.key}`;
    case 'internal':
      return `internal(${describeRule(rule.rule)})`;
    case 'anyOf':
    case 'allOf':
      return `${rule.kind}(${rule.rules.map(describeRule).join(',')})`;
    default: {
      const unhandled: never = rule;
      throw new Error(`Access rule without a description: ${JSON.stringify(unhandled)}`);
    }
  }
};

export const describeScope = (scope: Scope): string => (scope.kind === 'plant' ? `plant:${scope.plantId}` : scope.kind);

export const reportBranchWouldDeny = ({ decision, failed }: BranchDecision, pathname: string): void => {
  if (decision.allow) return;
  reportAccess({
    kind: 'would-deny',
    rule: failed ? describeRule(failed.rule) : 'undeclared',
    scope: failed ? describeScope(failed.scope) : pathname,
    reason: decision.reason,
  });
};
